import React, { useState, useMemo } from 'react';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';
import { PageHeader, StatsCards, FilterBar, ErrorState, type FilterConfig, type StatCard } from '../../components/Common';
import { useReportApi } from './useReportApi';
import { useDebouncedValue } from './useBanksReport';
import BillPaymentsDetails from './BillPaymentsDetails';

const fmt = (n: any) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Total deducted from an invoice, in the invoice's currency */
const deductionsTotal = (inv: any) =>
  (inv.deductions || [])
    .filter((d: any) => d.status !== 'cancelled')
    .reduce((sum: number, d: any) => sum + (Number(d.invoiceAppliedAmount ?? d.amount) || 0), 0);

const statusMap: Record<string, { label: string; cls: string }> = {
  paid: { label: 'مدفوع', cls: 'bg-green-100 text-green-800' },
  partially_paid: { label: 'مدفوع جزئياً', cls: 'bg-blue-100 text-blue-800' },
  sent: { label: 'مُرسل', cls: 'bg-indigo-100 text-indigo-800' },
  overdue: { label: 'متأخر', cls: 'bg-red-100 text-red-800' },
  draft: { label: 'مسودة', cls: 'bg-gray-100 text-gray-800' },
};

const typeMap: Record<string, { label: string; cls: string }> = {
  invoice_hire: { label: 'فاتورة إيجار', cls: 'bg-purple-100 text-purple-800' },
  invoice_accommodation: { label: 'فاتورة إعاشة', cls: 'bg-orange-100 text-orange-800' },
  debit_note: { label: 'إشعار مدين', cls: 'bg-red-100 text-red-800' },
  credit_note: { label: 'إشعار دائن', cls: 'bg-green-100 text-green-800' },
};

const InvoicesReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [vesselFilter, setVesselFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(searchTerm);
  const lookupQO = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: clientsData = [] } = useClients(lookupQO);
  const { data: branchesData = [] } = useBranches(lookupQO);

  const params = useMemo(() => ({
    status: statusFilter, client: clientFilter, vessel: vesselFilter,
    dateFrom, dateTo, search: debouncedSearch,
  }), [statusFilter, clientFilter, vesselFilter, dateFrom, dateTo, debouncedSearch]);

  const { summary, results, isLoading, error, refetch } = useReportApi('/invoices/report', params);

  const buildExportData = () => results.map((inv: any) => ({
    'Client': inv.client?.name || '-',
    'Type': typeMap[inv.type]?.label || inv.type || '-',
    'Vessel': inv.vessel?.name || '-',
    'FOA': inv.foa?.foaNumber || '-',
    'Description': inv.description || '-',
    'Currency': inv.currency?.code || '-',
    'Original Amount': inv.subtotal ?? inv.total,
    'Total': inv.total,
    'Performance Bond': inv.performanceBond || 0,
    'Total Without Bond': inv.totalWithoutBond,
    'Deductions': deductionsTotal(inv),
    'Paid': inv.paid,
    'Remaining': inv.remaining,
    'Status': inv.invoice_status,
    'Issue Date': inv.issueDate || '-',
    'Due Date': inv.dueDate || '-',
    'Payments Count': inv.paymentsCount || 0,
  }));

  const handleExport = () => exportToExcel(buildExportData(), `invoices_report_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Invoices Report');

  const handleClearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setClientFilter('all');
    setVesselFilter('all'); setDateFrom(''); setDateTo('');
  };

  const mainStats: StatCard[] = [
    { label: 'إجمالي الفواتير', value: summary?.totalInvoices ?? 0, color: 'blue' },
    { label: 'إجمالي المبلغ', value: fmt(summary?.totalAmount), color: 'yellow' },
    { label: 'إجمالي المدفوع', value: fmt(summary?.totalPaid), color: 'green' },
    { label: 'إجمالي المتبقي', value: fmt(summary?.totalRemaining), color: 'red' },
    { label: 'متوسط الفاتورة', value: fmt(summary?.averageInvoice), color: 'purple' },
  ];

  const currencyStats: StatCard[] = Object.entries(summary?.byCurrency || {}).map(([code, c]: [string, any]) => ({
    label: `${code} — عدد ${c.count}`,
    value: fmt(c.total),
    subtitle: `مدفوع: ${fmt(c.paid)} | متبقي: ${fmt(c.remaining)}`,
    color: 'blue' as const,
  }));

  // Totals per invoice type (invoices vs debit/credit notes), per currency
  const typeStats: StatCard[] = useMemo(() => {
    const groups: Record<string, { count: number; totals: Record<string, number> }> = {};
    results.forEach((inv: any) => {
      const t = inv.type || 'invoice_hire';
      const code = inv.currency?.code || 'EGP';
      if (!groups[t]) groups[t] = { count: 0, totals: {} };
      groups[t].count += 1;
      groups[t].totals[code] = (groups[t].totals[code] || 0) + (Number(inv.total) || 0);
    });
    return Object.entries(groups).map(([t, g]): StatCard => ({
      label: `${typeMap[t]?.label || t} — عدد ${g.count}`,
      value: Object.entries(g.totals).map(([c, v]) => `${fmt(v)} ${c}`).join(' + ') || '0',
      color: t === 'debit_note' ? 'red' : t === 'credit_note' ? 'green' : 'blue',
    }));
  }, [results]);

  const bondStats: StatCard[] = [
    { label: 'إجمالي التأمين (Performance Bond)', value: fmt(summary?.totalPerformanceBond), color: 'purple' },
    { label: 'إجمالي الفواتير بدون تأمين', value: fmt(summary?.totalWithoutBond), color: 'blue' },
  ];

  const filters: FilterConfig[] = [
    { key: 'status', label: 'الحالة', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'جميع الحالات' }, { value: 'draft', label: 'مسودة' }, { value: 'sent', label: 'مُرسل' },
      { value: 'partially_paid', label: 'مدفوع جزئياً' }, { value: 'paid', label: 'مدفوع' }, { value: 'overdue', label: 'متأخر' },
    ]},
    { key: 'client', label: 'العميل', type: 'select', value: clientFilter, onChange: setClientFilter, options: [
      { value: 'all', label: 'جميع العملاء' },
      ...clientsData.map((c: any) => ({ value: c.documentId || String(c.id), label: c.name })),
    ]},
    { key: 'vessel', label: 'السفينة', type: 'select', value: vesselFilter, onChange: setVesselFilter, options: [
      { value: 'all', label: 'جميع السفن' },
      ...(branchesData as any[]).map((b: any) => ({ value: b.documentId || String(b.id), label: b.name })),
    ]},
    { key: 'dateFrom', label: 'من تاريخ', type: 'date', value: dateFrom, onChange: setDateFrom },
    { key: 'dateTo', label: 'إلى تاريخ', type: 'date', value: dateTo, onChange: setDateTo },
  ];

  if (error) return <ErrorState message={error} onRetry={refetch} entityName="invoices report" />;

  const headers = ['العميل', 'النوع', 'السفينة', 'FOA', 'العملة', 'الأصلي', 'الإجمالي', 'التأمين', 'بدون تأمين', 'الخصومات', 'المدفوع', 'المتبقي', 'الحالة', 'تاريخ الإصدار', 'المدفوعات'];

  return (
    <div className="space-y-6" dir="rtl">
      <PageHeader title="تقرير الفواتير" description="عرض وتحليل جميع الفواتير مع المدفوعات والمتبقي لكل فاتورة" />
      <StatsCards stats={mainStats} columns={5} />
      {currencyStats.length > 0 && (
        <div>
          <h3 className="text-md font-semibold text-gray-800 mb-2">الإجماليات حسب العملة</h3>
          <StatsCards stats={currencyStats} columns={4} />
        </div>
      )}
      {typeStats.length > 0 && (
        <div>
          <h3 className="text-md font-semibold text-gray-800 mb-2">الإجماليات حسب النوع (فواتير / إشعارات)</h3>
          <StatsCards stats={typeStats} columns={4} />
        </div>
      )}
      <div>
        <h3 className="text-md font-semibold text-gray-800 mb-2">التأمين (Performance Bond)</h3>
        <StatsCards stats={bondStats} columns={3} />
      </div>
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="البحث في الوصف والملاحظات..."
        filters={filters} onClearFilters={handleClearFilters}
        totalCount={results.length} filteredCount={results.length} entityName="تصفية التقرير"
        onExport={handleExport} onPrint={handlePrint}
      />

      {isLoading ? <TableSkeleton /> : (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">نتائج التقرير ({results.length} فاتورة)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {headers.map(h => (
                    <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((inv: any) => {
                  const st = statusMap[inv.invoice_status] || { label: inv.invoice_status, cls: 'bg-yellow-100 text-yellow-800' };
                  const tp = typeMap[inv.type] || typeMap.invoice_hire;
                  const key = inv.documentId;
                  const isExpanded = expandedInvoice === key;
                  return (
                    <React.Fragment key={key}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{inv.client?.name || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tp.cls}`}>{tp.label}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{inv.vessel?.name || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{inv.foa?.foaNumber || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{inv.currency?.code || '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{fmt(inv.subtotal ?? inv.total)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{fmt(inv.total)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-purple-700 text-right">{fmt(inv.performanceBond)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{fmt(inv.totalWithoutBond)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-orange-700 text-right">
                          {deductionsTotal(inv) > 0 ? `- ${fmt(deductionsTotal(inv))}` : '0'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700 text-right">{fmt(inv.paid)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-red-700 text-right">{fmt(inv.remaining)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('ar-EG') : '-'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          <button type="button" onClick={() => setExpandedInvoice(isExpanded ? null : key)}
                            className="text-ocean-600 hover:text-ocean-800 font-medium">
                            {inv.paymentsCount || 0} {isExpanded ? '▲' : '▼'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={headers.length} className="px-4 py-2">
                            <BillPaymentsDetails payments={inv.payments || []} currencyCode={inv.currency?.code || ''} deductions={inv.deductions || []} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {results.length === 0 && (
            <div className="text-center py-8"><p className="text-gray-500">لا توجد فواتير تطابق المعايير المحددة</p></div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default InvoicesReport;
